import React, { useState, useCallback, useRef } from 'react';
import LookupModal from './LookupModal';
import { lookupSearch } from '../../services/FormsService';
import './LookupField.css';

/**
 * LookupField - 범용적인 참조 데이터 선택 컴포넌트
 * 다른 폼의 데이터를 검색해서 선택할 수 있는 기능 제공
 */
const LookupField = ({
  // 기본 필드 설정
  fieldName,
  fieldConfig,
  value,
  onChange,
  
  // Lookup 설정 (JSON에서 가져옴)
  lookupConfig = {},
  
  // UI 설정
  disabled = false,
  placeholder = "",
  className = "",
  
  // 에러 상태
  error = null,
  
  // 모달 열림 콜백
  onModalOpen = null
}) => {
  // 상태 관리
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [displayText, setDisplayText] = useState('');
  
  // Lookup 설정 추출
  const {
    source_form_id,
    search_endpoint = '/api/forms/form_lookup/',
    display_field,
    value_field,
    description_field,
    search_fields = [],
    result_template,
    modal_title = "항목 선택"
  } = lookupConfig;
  
  // **🎯 LookupModal용 검색 함수**
  const performLookupSearch = useCallback(async (searchQuery = '') => {
    if (!source_form_id) {
      console.error('Lookup 설정에 source_form_id가 없습니다.');
      return [];
    }
    
    try {
      const response = await lookupSearch({
        source_form_id,
        search_query: searchQuery,
        search_fields,
        limit: 50
      });
      
      if (response.success) {
        return response.options || [];
      } else {
        console.error('Lookup 검색 실패:', response.error);
        return [];
      }
    } catch (error) {
      console.error('Lookup 검색 오류:', error);
      return [];
    }
  }, [source_form_id, search_fields]);
  
  // 모달 열기
  const handleOpenModal = useCallback(() => {
    // 부모 컴포넌트에 모달 열림 알림
    if (onModalOpen) {
      onModalOpen();
    }
    setIsModalOpen(true);
  }, [onModalOpen]);
  
  // 모달 닫기
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);
  
  // 항목 선택 처리
  const handleSelectOption = useCallback((option) => {
    if (!option) return;
    
    console.log('LookupField: 선택된 옵션:', option);
    console.log('LookupField: value_field:', value_field);
    
    // 값 설정
    const newValue = option[value_field];
    console.log('LookupField: 추출된 값:', newValue);
    
    // **🎯 항상 3개 인자로 onChange 호출 (FormRenderer가 이를 처리)**
    if (typeof onChange === 'function') {
      console.log('LookupField: onChange 호출 - fieldName:', fieldName, 'value:', newValue, 'option:', option);
      onChange(fieldName, newValue, option);
    }
    
    // 선택된 옵션 저장
    setSelectedOption(option);
    
    // 표시 텍스트 생성
    let newDisplayText = '';
    if (result_template) {
      // 템플릿 기반 표시 (예: "{car_number} ({client_name})")
      newDisplayText = result_template.replace(/\{(\w+)\}/g, (match, fieldName) => {
        return option[fieldName] || '';
      });
    } else if (display_field) {
      // 단순 필드 표시
      newDisplayText = option[display_field] || '';
    } else {
      // 기본 표시
      newDisplayText = String(newValue);
    }
    
    setDisplayText(newDisplayText);
    setIsModalOpen(false);
  }, [onChange, value_field, display_field, result_template, fieldName]);
  
  // 값 초기화
  const handleClear = useCallback(() => {
    onChange(null);
    setSelectedOption(null);
    setDisplayText('');
  }, [onChange]);
  

  
  // 초기값 설정 효과
  React.useEffect(() => {
    if (value && !selectedOption) {
      // 값이 있지만 선택된 옵션이 없는 경우 (초기 로딩)
      setDisplayText(String(value)); // 임시로 값 자체를 표시
    } else if (!value) {
      // 값이 없으면 초기화
      setSelectedOption(null);
      setDisplayText('');
    }
  }, [value, selectedOption]);
  
  // CSS 클래스 조합
  const containerClasses = [
    'lookup-field-container',
    className,
    error ? 'has-error' : '',
    disabled ? 'disabled' : ''
  ].filter(Boolean).join(' ');
  
  return (
    <div className={containerClasses}>
      {/* 입력 필드 */}
      <div className="lookup-input-group">
        <input
          type="text"
          className="lookup-display-input"
          placeholder={placeholder || `${fieldConfig?.title || fieldName} 선택`}
          value={displayText}
          readOnly
          disabled={disabled}
          onClick={!disabled ? handleOpenModal : undefined}
        />
        
        {/* 버튼 그룹 */}
        <div className="lookup-button-group">
          <button
            type="button"
            className="lookup-search-btn"
            onClick={handleOpenModal}
            disabled={disabled}
            title="검색 및 선택"
          >
            🔍
          </button>
          
          {value && (
            <button
              type="button"
              className="lookup-clear-btn"
              onClick={handleClear}
              disabled={disabled}
              title="선택 해제"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      
      {/* 에러 메시지 */}
      {error && (
        <div className="lookup-error-message">
          {error}
        </div>
      )}
      
      {/* **🎯 통합 Lookup 모달** */}
      <LookupModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSelect={handleSelectOption}
        searchFunction={performLookupSearch}
        title={modal_title}
        displayFields={[
          { field: display_field, label: fieldConfig?.title || '표시명' },
          { field: value_field, label: 'ID' },
          ...(description_field ? [{ field: description_field, label: '설명' }] : [])
        ]}
        placeholder="검색어를 입력하세요..."
      />
    </div>
  );
};

export default LookupField; 